import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { cx } from '../cx';
import { formatNumber, type DsLocale } from '../format';

export type ChipLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children' | 'href'> & {
  /** Without an href the chip is drawn dimmed and not pressable (a letter that has no entries). */
  href?: string;
  children: ReactNode;
  /** This chip is the page you are on: filled with the selected colour, `aria-current="page"`. */
  current?: boolean;
  /** An optional count after the label, formatted for the page language. */
  count?: number;
  locale?: DsLocale;
};

/**
 * A link that looks like a FilterChip: a 40px pill that goes to another page (an A to Z letter, a
 * city or industry in a browse list, a view tab). FilterChip toggles a filter in place and is a
 * button; ChipLink navigates and is an anchor, so crawlers and no-JavaScript visitors still follow it.
 */
export function ChipLink({ href, children, current = false, count, locale = 'en', className, ...rest }: ChipLinkProps) {
  const base = cx(
    'inline-flex h-10 min-w-10 items-center justify-center gap-1.5 rounded-jc-control px-4 text-sm font-semibold whitespace-nowrap',
    className,
  );
  const label = (
    <>
      <span>{children}</span>
      {count !== undefined ? <span className="tabular-nums opacity-75">{formatNumber(count, locale)}</span> : null}
    </>
  );
  if (!href) {
    return (
      <span {...rest} aria-disabled="true" className={cx(base, 'text-jc-ink-secondary opacity-50')}>
        {label}
      </span>
    );
  }
  return (
    <a
      {...rest}
      href={href}
      aria-current={current ? 'page' : undefined}
      className={cx(
        base,
        'transition-colors duration-(--jc-duration-fast) ease-jc-out motion-reduce:transition-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus focus-visible:ring-offset-2 focus-visible:ring-offset-jc-page',
        current
          ? 'bg-jc-selected text-jc-on-selected'
          : 'bg-jc-surface text-jc-ink ring-1 ring-inset ring-jc-border-control hover:bg-jc-elevated',
      )}
    >
      {label}
    </a>
  );
}
